import { Box, Checkbox, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { PRIO_COLORS, TYPE_COLORS } from '../utils/taakKleuren';

/**
 * Het gedeelde taakblok voor de planningsschermen: kleurstreep op prio,
 * naam (doorgestreept als klaar), en compacte badges voor uren/deadline/type.
 *
 * Bewust presentatie-only en drag-agnostisch: het taskboard wikkelt hem in een
 * dnd-kit-sortable, andere schermen in hun eigen interactie. De Dagstart en
 * Planner To-Do houden (nog) hun eigen blok — die zijn vergroeid met hun
 * pointer-drag; zij delen wél dezelfde kleurenkaart (utils/taakKleuren.js).
 */
const dagenTot = (deadline) => {
  if (!deadline) return null;
  const d = new Date(String(deadline).slice(0, 10));
  const nu = new Date(); nu.setHours(0, 0, 0, 0);
  return Math.round((d - nu) / 86400000);
};

function TaakBlok({
  naam,
  prio = '',
  type = '',
  uren = 0,
  deadline = null,
  deadlineType = '',
  isKlaar = false,
  onToggleKlaar = null,
  onClick = null,
  rechts = null,   // extra content (iconen/badges) aan de rechterkant
  sx = {},
}) {
  const prioKleur = PRIO_COLORS[prio] || '#94a3b8';
  const typeKleur = TYPE_COLORS[type] || '#6b7280';
  const dDagen = dagenTot(deadline);

  return (
    <Box onClick={onClick} sx={{
      display: 'flex', alignItems: 'center', gap: 0.75,
      px: 1, py: 0.75, borderRadius: 1.5,
      bgcolor: '#fff', border: '1px solid #e5e7eb',
      borderLeft: `3px solid ${prioKleur}`,
      cursor: onClick ? 'pointer' : 'default',
      opacity: isKlaar ? 0.65 : 1,
      '&:hover': { borderColor: '#c7d2fe' },
      ...sx,
    }}>
      {onToggleKlaar && (
        <Checkbox
          checked={isKlaar}
          onChange={(e) => { e.stopPropagation(); onToggleKlaar(); }}
          onClick={(e) => e.stopPropagation()}
          icon={<RadioButtonUncheckedIcon sx={{ fontSize: 16, color: '#d1d5db' }} />}
          checkedIcon={<CheckIcon sx={{ fontSize: 16, color: '#10b981' }} />}
          sx={{ p: 0, mr: 0.25 }}
          size="small"
        />
      )}

      <Typography variant="body2" title={naam} sx={{
        flex: 1, minWidth: 0, fontSize: '0.8rem', fontWeight: 500, lineHeight: 1.3,
        display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
        overflow: 'hidden', overflowWrap: 'anywhere',
        textDecoration: isKlaar ? 'line-through' : 'none',
        color: isKlaar ? '#9ca3af' : '#1f2937',
      }}>
        {naam || 'Naamloos'}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {uren > 0 && (
          <Tooltip title={`${parseFloat(uren).toFixed(1)} uur`} arrow>
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600 }}>
              {parseFloat(uren) % 1 === 0 ? Math.round(uren) : parseFloat(uren).toFixed(1)}h
            </Typography>
          </Tooltip>
        )}
        {deadline && (
          <Tooltip title={`Deadline: ${String(deadline).slice(0, 10)}${deadlineType ? ` (${deadlineType})` : ''}`} arrow>
            <Typography variant="caption" sx={{
              fontSize: '0.62rem', fontWeight: 700, px: 0.5, borderRadius: 0.75,
              color: dDagen !== null && dDagen < 0 ? '#991b1b' : dDagen !== null && dDagen <= 2 ? '#9a3412' : '#6b7280',
              bgcolor: dDagen !== null && dDagen < 0 ? '#fee2e2' : dDagen !== null && dDagen <= 2 ? '#ffedd5' : '#f3f4f6',
            }}>
              {dDagen !== null && dDagen < 0 ? `${-dDagen}d over` : dDagen === 0 ? 'vandaag' : `${dDagen}d`}
            </Typography>
          </Tooltip>
        )}
        {type && (
          <Tooltip title={type} arrow>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: typeKleur }} />
          </Tooltip>
        )}
        {rechts}
      </Box>
    </Box>
  );
}

export default TaakBlok;
